import MoneyCloseCheckPage from './dashboard/money/MoneyCloseCheckPage';
import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AppLayout from './layout/AppLayout';
import Home from './home/Home';
import LoginPage from './auth/LoginPage';
import AuthCallbackPage from './auth/AuthCallbackPage';
import WalkinApp from './walkin/WalkinApp';
import DashboardLayout from './dashboard/DashboardLayout';
import RequirePatientIntakeMode from './dashboard/appointments/RequirePatientIntakeMode';
import RequireNavSection from './dashboard/RequireNavSection';
import RequireFollowupQueue from './dashboard/coc/RequireFollowupQueue';
import RequireNoShowQueue from './dashboard/coc/RequireNoShowQueue';
import StaffOtpLoginPage from './dashboard/StaffOtpLoginPage';
import { fetchDashboardMe } from './dashboard/api';
import {
  dashboardAuthErrorView,
  type DashboardAuthErrorView,
} from './dashboard/auth-error-view';
import OwnerHomePage from './dashboard/owner/home/OwnerHomePage';
import OwnerMoneyConsolePage from './dashboard/owner/money-console/OwnerMoneyConsolePage';
import CatalogRowMappingPage from './dashboard/config/CatalogRowMappingPage';
import ClinicReportsPage from './dashboard/clinic-reports/ClinicReportsPage';
import OwnerReportsPage from './dashboard/owner/reports/OwnerReportsPage';
import SourceToRevenuePage from './dashboard/owner/reports/SourceToRevenuePage';
import OwnerLeakageBoardPage from './dashboard/owner/leakage/OwnerLeakageBoardPage';
import ContactTimelinePage from './dashboard/owner/ContactTimelinePage';
import OwnerLeadsListRoute from './dashboard/owner/OwnerLeadsListRoute';
import LeadDetailPage from './dashboard/owner/LeadDetailPage';
import WorkRoutingQaPage from './dashboard/owner/WorkRoutingQaPage';
import RequireLeadsWorkspace from './dashboard/leads/RequireLeadsWorkspace';
import LeadsWorkspacePage from './dashboard/leads/LeadsWorkspacePage';
import LeadsWorkspaceDetailPage from './dashboard/leads/LeadsWorkspaceDetailPage';
import RequireRoutingSettings from './dashboard/work-routing/RequireRoutingSettings';
import WorkRoutingSettingsPage from './dashboard/work-routing/WorkRoutingSettingsPage';
import AppointmentCalendarPage, {
  RequireAppointmentCalendar,
} from './dashboard/appointments/AppointmentCalendarPage';
import RequireWorkboard from './dashboard/workboard/RequireWorkboard';
import WorkboardPage from './dashboard/workboard/WorkboardPage';
import RequireBillingEntry from './dashboard/billing/RequireBillingEntry';
import RequireMoneyDesk from './dashboard/money/RequireMoneyDesk';
import MoneyDeskPage from './dashboard/money/MoneyDeskPage';
import BillingEntryPage from './dashboard/billing/BillingEntryPage';
import RequireClinicalWorkbench from './dashboard/clinical-workbench/RequireClinicalWorkbench';
import ClinicalWorkbenchPage from './dashboard/clinical-workbench/ClinicalWorkbenchPage';
import RequireTreatmentCatalog from './dashboard/catalog/RequireTreatmentCatalog';
import TreatmentCatalogPage from './dashboard/catalog/TreatmentCatalogPage';
import RequireLeadCockpit from './dashboard/lead-cockpit/RequireLeadCockpit';
import LeadToAppointmentCockpit from './dashboard/lead-cockpit/LeadToAppointmentCockpit';
import RequireCallCenterWorkspace from './dashboard/callcenter/RequireCallCenterWorkspace';
import CallCenterWorkspacePage from './dashboard/callcenter/CallCenterWorkspacePage';
import CallCenterDailyReportPage from './dashboard/callcenter/daily-report/CallCenterDailyReportPage';
import RequireCallCenterDailyReport from './dashboard/callcenter/daily-report/RequireCallCenterDailyReport';
import RequireOrgIntake from './dashboard/org-intake/RequireOrgIntake';
import OrgIntakePage from './dashboard/org-intake/OrgIntakePage';
import RequireLeadInbox from './dashboard/lead-inbox/RequireLeadInbox';
import LeadInboxPage from './dashboard/lead-inbox/LeadInboxPage';
import RequireTestJourney from './dashboard/journey/RequireTestJourney';
import TestJourneyPage from './dashboard/journey/TestJourneyPage';
import RequirePatientMaster, {
  RequirePatientMasterMerge,
} from './dashboard/patients/RequirePatientMaster';
import PatientSearchPage from './dashboard/patients/PatientSearchPage';
import PatientCreatePage from './dashboard/patients/PatientCreatePage';
import ConsentFormPrintPage from './dashboard/patients/ConsentFormPrintPage';
import PatientRecordPage from './dashboard/patients/PatientRecordPage';
import PatientTimelinePage from './dashboard/patients/PatientTimelinePage';
import PatientMergeQueuePage from './dashboard/patients/PatientMergeQueuePage';
import CocDashboardPage from './dashboard/coc/CocDashboardPage';
import ClinicStatusPage from './dashboard/coc/ClinicStatusPage';
import NoShowRecoveryPage from './dashboard/coc/NoShowRecoveryPage';
import PatientFollowupsPage from './dashboard/coc/PatientFollowupsPage';
import BillingCompliancePage from './dashboard/coc/BillingCompliancePage';
import CocFollowupReportPage from './dashboard/coc/CocFollowupReportPage';
import CocDailyReportPage from './dashboard/coc/CocDailyReportPage';
import RequireManagerTracking from './dashboard/manager-tracking/RequireManagerTracking';
import AgmDashboardPage from './dashboard/agm/AgmDashboardPage';
import ZonePerformancePage from './dashboard/agm/ZonePerformancePage';
import ClinicComparisonPage from './dashboard/agm/ClinicComparisonPage';
import DoctorPortalPage from './dashboard/doctor/DoctorPortalPage';
import DoctorPendingPage from './dashboard/doctor/DoctorPendingPage';
import ConsultWorkspacePage from './dashboard/doctor/ConsultWorkspacePage';
import TreatmentConsentPrintPage from './dashboard/patients/TreatmentConsentPrintPage';
import RequireDoctorLens from './dashboard/doctor/RequireDoctorLens';
import DoctorPatientsPage from './dashboard/doctor/DoctorPatientsPage';
import DoctorPatientLensPage from './dashboard/doctor/DoctorPatientLensPage';

/**
 * Role-aware redirect rendered at `/dashboard`. Fetches
 * `/api/dashboard/me` and navigates to the user's role-specific home
 * (owner → /dashboard/owner, COC → /dashboard/coc, etc.). While the
 * fetch is in flight we show a small status message instead of forcing
 * everyone through the owner dashboard.
 *
 * On error we show a sign-in prompt rather than redirecting — a
 * silent redirect could leak access boundaries (e.g. send a doctor to
 * /dashboard/owner just to receive an access-restricted card).
 */
function DashboardHomeRedirect(): JSX.Element {
  const navigate = useNavigate();
  const [authView, setAuthView] = useState<DashboardAuthErrorView | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDashboardMe()
      .then((me) => {
        if (cancelled) return;
        navigate(me.homePath, { replace: true });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Reason-aware: a NO_PROFILE 401 means the user IS signed in but not
        // provisioned, so the card must not tell them to sign in again.
        setAuthView(dashboardAuthErrorView(err));
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (authView) {
    return (
      <div
        className="rounded-md border border-border bg-muted/40 px-4 py-3 text-sm"
        role="status"
      >
        <p className="font-medium text-foreground">{authView.title}</p>
        <p className="mt-1 text-muted-foreground">{authView.body}</p>
        {authView.showSignIn ? (
          <Link
            to="/login"
            className="mt-2 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        ) : null}
      </div>
    );
  }

  return (
    <p className="loading" role="status" aria-live="polite">
      Opening dashboard…
    </p>
  );
}

/**
 * APP-ROUTE-ERROR-BOUNDARY-V1 -- TEST-ONLY crash probe. Throws during
 * render on purpose so the e2e walk can prove the route-level error
 * boundary contains a render crash inside dashboard-main while the shell
 * survives. DEV BUILDS ONLY: the route below mounts behind a literal
 * `import.meta.env.DEV` conditional, which `vite build` statically
 * replaces with `false`, so this component AND its route are dead code
 * eliminated from every production bundle (the dev-token-seam precedent:
 * tests/e2e/role-acceptance uses the same DEV-only posture; absence is
 * verified by grepping the built dist/ for the marker string). Renders
 * nothing, fetches nothing, grants no role anything (doctrine 06: no new
 * door -- an unguarded child of /dashboard like config/catalog-rows, but
 * with NO backend read at all).
 */
function BoundaryCrashProbe(): JSX.Element {
  throw new Error(
    'ROUTE-ERROR-BOUNDARY-PROBE: deliberate render-phase crash (dev/test builds only)',
  );
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/home" element={<Home />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      {/*
        PATIENT-INTAKE-PREFILL-AND-EXPERIENCE-V1 (Section 8): the full-screen
        PATIENT / TABLET intake mode. Deliberately a TOP-LEVEL route -- OUTSIDE
        AppLayout (its header carries a Dashboard link) and DashboardLayout (the
        staff nav) -- so no staff navigation, patient/lead/billing lists, or
        other patient data can render around it. Authenticated via the existing
        session only (the guard bounces an unauthenticated hit to /login); no
        new auth, no public token, no QR. The backend route is the security
        boundary.
      */}
      <Route path="/patient-intake/:appointmentId" element={<RequirePatientIntakeMode />} />
      <Route element={<AppLayout />}>
        {/*
          S2-P5C: /walkin renders inside the SAME shell as the dashboard pages.
          Nesting it under a (pathless) DashboardLayout gives it the top header
          AND the left nav + the role-scoped `me` context (DashboardMeProvider),
          instead of the prior bare-AppLayout centred-card surface. The URL stays
          `/walkin` (the receptionist home), and no route guard wraps it - any
          signed-in staff member may register a walk-in (backend route auth
          remains the security boundary).
        */}
        <Route element={<DashboardLayout />}>
          <Route path="/walkin/*" element={<WalkinApp />} />
        </Route>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/staff-login" element={<StaffOtpLoginPage />} />
        <Route path="/dashboard" element={<DashboardLayout />}>
          <Route index element={<DashboardHomeRedirect />} />
          <Route path="clinic-reports" element={<ClinicReportsPage />} />
          {/* TEST-ONLY (APP-ROUTE-ERROR-BOUNDARY-V1): the DEV-only crash
              probe -- absent from production bundles (see
              BoundaryCrashProbe above). */}
          {import.meta.env.DEV ? (
            <Route path="__boundary-probe" element={<BoundaryCrashProbe />} />
          ) : null}
          {/*
            Role-aware route guards: each role-scoped group is wrapped
            with `<RequireNavSection>` so a lower role hitting a higher
            role's URL is redirected to their own `me.homePath` BEFORE
            the role-specific page renders. The page's data-fetching
            `useEffect` never fires; the backend never has to 403; and
            no owner card flashes before the redirect. Backend route
            auth still enforces the real boundary — this guard only
            closes the UX gap.
          */}
          <Route element={<RequireNavSection section="owner" />}>
            {/*
              OWNER-OPERATING-SYSTEM-V1 (SURVIVORSHIP RULING: REPLACES + the
              HOME RULING): Home replaces the owner Overview. The Overview
              page is DELETED; its route SURVIVES as a permanent redirect so
              bookmarks never 404 (the owner-route-survival pin; the
              OwnerLeadsListRoute shim precedent -- an SPA's 301). Growth /
              Analytics / Clinics fold into Reports per the tenancy-audit
              verdicts (rows 11/18/19/20) and the HOME RULING ("detailed
              analysis remains under Reports"); Staff's stub retires to the
              routing-settings surface that hosted its only door (row 27).
            */}
            <Route path="home" element={<OwnerHomePage />} />
            <Route path="reports" element={<OwnerReportsPage />} />
            <Route
              path="reports/source-to-revenue"
              element={<SourceToRevenuePage />}
            />
            {/* OWNER-MONEY-CONSOLE-V1 (M6): the daily money console lives
                under Reports (nav rule 3 -- no seventh owner nav row; the
                HOME RULING's analysis home). Server-fenced owner-only. */}
            <Route
              path="reports/money-console"
              element={<OwnerMoneyConsolePage />}
            />

            <Route path="leakage" element={<OwnerLeakageBoardPage />} />
            <Route
              path="owner"
              element={<Navigate to="/dashboard/home" replace />}
            />
            <Route
              path="owner/clinics"
              element={<Navigate to="/dashboard/reports" replace />}
            />
            <Route
              path="owner/clinics/:id"
              element={<Navigate to="/dashboard/reports" replace />}
            />
            {/*
              LEADS-CANONICAL-FOLD-V1 (V5-P10): the owner Leads LIST folds into
              the ONE canonical list (/dashboard/leads). Flag-aware shim -- when
              the canonical Leads Workspace is live for this owner it redirects
              there; with the flag off it renders the legacy owner list
              (byte-identical to today, no homePath bounce). Route SURVIVES as a
              redirect so bookmarks never 404 (owner-route-survival pin). Mirrors
              the sibling detail shim (owner/LeadDetailPage.tsx; V1-P3).
            */}
            <Route path="owner/leads" element={<OwnerLeadsListRoute />} />
            <Route
              path="owner/leads/:safeLeadId"
              element={<LeadDetailPage />}
            />
            <Route
              path="owner/analytics"
              element={<Navigate to="/dashboard/reports" replace />}
            />
            <Route
              path="owner/growth"
              element={<Navigate to="/dashboard/reports" replace />}
            />
            <Route
              path="owner/staff"
              element={<Navigate to="/dashboard/work-routing/settings" replace />}
            />
            <Route
              path="owner/contact-timeline"
              element={<ContactTimelinePage />}
            />
            {/*
              WORK-ROUTING-QA-PAGE-BUILD-DOCS-RUNBOOK-01: internal,
              owner-scoped work-routing QA surface. The owner nav guard
              role-scopes the route (non-owners redirect to their own
              homePath); the page itself renders a disabled state and fires
              NO QA request unless the server-computed
              `me.workRoutingQaEnabled` signal is present (visible-QA flag
              on in the deployed backend + owner). QA only — not the
              production work queue, not a demo surface. Backend route auth
              (503 before auth + requireOwnerScope) remains the boundary.
            */}
            <Route
              path="owner/work-routing-qa"
              element={<WorkRoutingQaPage />}
            />
          </Route>
          {/*
            LEADS-WORKSPACE-ROLE-SCOPED-01: role-scoped Leads Workspace
            (owner / COC / receptionist). Guarded by the SERVER-computed
            `me.leadsWorkspaceEnabled` signal — with the backend flag
            off (or for any other role) the guard redirects to the
            user's own homePath, so nothing new is reachable and no UI
            changes. Backend route auth remains the security boundary.
          */}
          {/*
            ORG-CONFIG-ACCESS-AND-PRODUCT-MAPPING-V1: the reporting-row mapping
            configuration surface.

            DELIBERATELY UNGATED ON THE CLIENT, and NOT inside the owner
            RequireNavSection group. Access is an ORGANIZATION-SCOPED CONFIG
            CAPABILITY resolved from data (config_management.authorized_roles),
            so WHICH roles may enter differs per tenant -- a client nav-section
            guard keyed to a role would bounce a capability-holding GM/AGM
            before the page could render, and would have to be edited for every
            tenant that grants the capability differently. The server wire is
            the security boundary (requireConfigCapability), and the page
            renders an honest refusal when the server denies -- the /walkin
            precedent above: no route guard, backend route auth remains the
            boundary.
          */}
          <Route
            path="config/catalog-rows"
            element={<CatalogRowMappingPage />}
          />

          <Route element={<RequireLeadsWorkspace />}>
            <Route path="leads" element={<LeadsWorkspacePage />} />
            <Route
              path="leads/:safeLeadId"
              element={<LeadsWorkspaceDetailPage />}
            />
          </Route>
          {/*
            S1-P6-WORK-QUEUE-ROUTING-SETTINGS-ROLE-ONLY-BUILD-01: the Work
            Routing Settings surface. Role-only gate (owner / gm_agm /
            regional_head) via RequireRoutingSettings - a lower role hitting the
            URL redirects to its own homePath BEFORE the page renders. NO /me
            flag, NO feature flag. Backend route auth (canConfigureRouting + the
            row-level org-default / clinic-scope policy) remains the boundary.
          */}
          <Route element={<RequireRoutingSettings />}>
            <Route
              path="work-routing/settings"
              element={<WorkRoutingSettingsPage />}
            />
          </Route>
          {/*
            C2-WORKBOARD-UI-BUILD-01: the front-half operator Workboard. Role-only
            gate (receptionist / coc) via RequireWorkboard - a lower role hitting
            the URL redirects to its own homePath BEFORE the page renders. NO /me
            flag, NO feature flag, NO new nav section (internal surfaces ship
            default-on per ADR-0016). Backend route auth on /api/me/work-actions
            and /api/leads/:id/stage remains the security boundary.
          */}
          <Route element={<RequireWorkboard />}>
            <Route path="workboard" element={<WorkboardPage />} />
          </Route>
          {/*
            APPOINTMENT-BOOK-V1: the Appointments PLANNING screen (read-only,
            single-day, date-navigable, per-doctor columns). Role-only gate via
            RequireAppointmentCalendar, whose decision keys off the EXISTING
            canSeeDayBoard mirror of the backend DAY_BOARD_ROLES set -- so the
            RECEPTIONIST reaches it from the nav and coc / owner / gm_agm /
            regional_head read it DOWN by URL/drill WITHOUT a nav row of their
            own (doctrine rule 3; the Team-operations / Treatment-catalog
            read-down precedent). NO /me flag, NO feature flag, NO new nav
            section, NO new role set (internal surfaces ship default-on per
            ADR-0016 Decision 9). Backend route auth on
            GET /api/appointments/today?view=board remains the boundary.
          */}
          <Route element={<RequireAppointmentCalendar />}>
            <Route path="appointments" element={<AppointmentCalendarPage />} />
          </Route>
          {/*
            PATIENT-PAYMENTS-REVENUE-SPINE-V1 (Epic 9 rung 2): the billing
            payment-entry surface. Role-only gate (receptionist / coc / owner
            - the seeded entry role + supervisors) via RequireBillingEntry -
            a lower role hitting the URL redirects to its own homePath BEFORE
            the page renders. NO /me flag, NO feature flag, NO new nav
            section (internal surface, default-on per ADR-0016). Backend
            route auth on /api/patient-payments* (the CONFIG-BACKED
            billing_entry routing gate + org/clinic scope) remains the
            security boundary.
          */}
          <Route element={<RequireBillingEntry />}>
            <Route path="billing-entry" element={<BillingEntryPage />} />
          </Route>
          {/*
            ACCOUNTS-PHASE-1-BUILD-V1: the front-desk Money workspace (the
            daily cash spine; brief screens 6.1-6.3). Role-only gate
            (receptionist = the seeded counter role; coc / owner supervise)
            via RequireMoneyDesk - a lower role hitting the URL redirects to
            its own homePath BEFORE the page renders. NO /me flag, NO feature
            flag (internal operator surface, default-on per ADR-0016).
            Backend route auth on /api/accounts/* (the CONFIG-BACKED C6
            counter role + coc/owner + the C12 all-branches seam, org/clinic
            fenced) remains the security boundary.
          */}
          {/* Assigned check only: backend enforces scope and separate identity;
              this route grants no Doctor access to the Money workspace. */}
          <Route path="money-check" element={<MoneyCloseCheckPage />} />
          <Route element={<RequireMoneyDesk />}>
            <Route path="money" element={<MoneyDeskPage />} />
          </Route>
          {/*
            BACK-HALF-CLINICAL-WORKBENCH-UI-01: the back-half clinical workbench.
            Role-only gate (coc / doctor record the categorical outcome; owner
            confirms revenue + sees follow-ups) via RequireClinicalWorkbench - a
            lower role hitting the URL redirects to its own homePath BEFORE the
            page renders. NO /me flag, NO feature flag, NO new nav section
            (internal surface, default-on per ADR-0016). Backend route auth on
            /api/appointments/:id/{treatment-*} (OUTCOME role gate) and
            /api/leads/:id/stage (STAGE_CONTRACTS) remains the security boundary.
          */}
          <Route element={<RequireClinicalWorkbench />}>
            <Route
              path="clinical-workbench"
              element={<ClinicalWorkbenchPage />}
            />
          </Route>
          {/*
            CLINIC-CATALOG-PRICING-TRUTH-S1-V1: the treatment-catalog browse
            (read truth over the seeded price master). Role-only gate (the
            EXISTING catalog audience: doctor + owner/gm_agm/regional_head --
            the frozen backend TREATMENT_CATALOG_READ_ROLES mirror) via
            RequireTreatmentCatalog - a lower role hitting the URL redirects
            to its own homePath BEFORE the page renders. NO /me flag, NO
            feature flag, NO new visibility mechanism (internal master-data
            surface, default-on per ADR-0016; clinic-scoped resolution IS the
            existing tenancy fence). Backend route auth on GET
            /api/treatment-catalog (role gate BEFORE DB; org fence;
            scope-honest clinic 403) remains the security boundary.
          */}
          <Route element={<RequireTreatmentCatalog />}>
            <Route
              path="treatment-catalog"
              element={<TreatmentCatalogPage />}
            />
          </Route>
          {/*
            LEAD-TO-APPOINTMENT-COCKPIT-V1: the agent/COC lead-to-appointment
            operating cockpit. Role-only gate (receptionist / coc, the front-half
            operator pair) via RequireLeadCockpit - a lower role hitting the URL
            redirects to its own homePath BEFORE the page renders. NO /me flag,
            NO feature flag, NO new nav section (internal surface, default-on per
            ADR-0016). It COMPOSES existing backend only (POST
            /api/appointments/booked, POST /api/leads/:id/stage, GET
            /api/me/work-actions, GET /api/leads/workspace) - backend route auth
            on each remains the security boundary.
          */}
          <Route element={<RequireLeadCockpit />}>
            <Route
              path="lead-cockpit"
              element={<LeadToAppointmentCockpit />}
            />
          </Route>
          {/*
            CALL-CENTER-CRM-WORKSPACE-V1: the one-queue call-center workspace
            (agent "My Work" own-scope lens; manager "Team operations" team
            lens -- ONE route, the server picks the lens by role). Guarded by
            RequireCallCenterWorkspace on the SERVER-computed /me
            callCenterWorkspaceEnabled signal (per-org
            callcenter.workspace_enabled config row, seeded OFF, x the
            workspace audience call_center_agent | owner | gm_agm |
            regional_head) -- gate off or an unauthorized role redirects to
            its own homePath BEFORE the page renders (invisible, never a dead
            door). NO env flag (a per-org config gate). Backend route auth on
            GET /api/callcenter/workspace re-checks gate + audience + scope
            and remains the security boundary.
          */}
          <Route element={<RequireCallCenterWorkspace />}>
            <Route
              path="callcenter/workspace"
              element={<CallCenterWorkspacePage />}
            />
          </Route>
          {/*
            CALLCENTRE-EOD-V1: the call-centre agent's date-navigable Daily
            report (the SLA/lead-chase slice of activity 13) -- flag-free,
            unlike My Work above (reuses getSlaLeakage + loadChaseQueue, both
            already open to call_center_agent with no config gate).
            AGENT-DAY-PRODUCTIVITY-ROLLUP-V1 (activity 11) later composed the
            leadership tier's team rollup onto this SAME route server-side
            (CALLCENTER_DAILY_ROLLUP_AUDIENCE_ROLES = owner / gm_agm /
            regional_head), but the route stayed behind RequireNavSection
            section="call_center" -- a nav-section guard only call_center_agent
            carries (dashboardNavSectionsForRole) -- so the rollup was built
            and tested yet unreachable by any manager's browser.
            MANAGER-ROLLUP-REACHABILITY-V1 (activity 11 closure) replaces that
            guard with RequireCallCenterDailyReport, the manager-tracking
            precedent (RequireManagerTracking) applied here: a per-surface
            role-set gate keyed off the combined
            CALLCENTER_DAILY_REPORT_ROUTE_AUDIENCE_ROLES set, decoupled from
            nav-section membership. call_center_agent's own-day view is
            byte-identical; only owner, gm_agm, and regional_head are newly
            admitted. Backend route auth on GET /api/callcenter/daily-report
            (the union of CALLCENTER_DAILY_REPORT_AUDIENCE_ROLES and
            CALLCENTER_DAILY_ROLLUP_AUDIENCE_ROLES) remains the security
            boundary; this guard is UX cleanliness only.
          */}
          <Route element={<RequireCallCenterDailyReport />}>
            <Route
              path="callcenter/daily-report"
              element={<CallCenterDailyReportPage />}
            />
          </Route>
          {/*
            ORG-INTAKE-QUEUE-V1 (B2, Story 5.4): the org-intake
            pending-clinic queue (initially configured for COC in the
            Partha workflow). The guard admits the ROUTABLE role superset
            (the queue's working role is CONFIG-RESOLVED server-side from
            the org-default new_lead_followup routing_config row; default
            'coc'; owner supervises org-wide) - a routable role that is
            not the configured owner gets the backend's 401, rendered as
            the honest access-restricted state. NO /me flag, NO feature
            flag, NO new nav section (internal surface, default-on per
            ADR-0016; the queue is empty until the B1 go-live flags flip).
            Backend route auth on GET /api/org-intake/queue and POST
            /api/leads/:id/set-clinic remains the security boundary.
          */}
          <Route element={<RequireOrgIntake />}>
            <Route path="org-intake" element={<OrgIntakePage />} />
          </Route>
          {/*
            ALL-SOURCE-LEAD-INBOX-V1 (B3, Story 2.2): the org-level
            all-source lead inbox (read-only triage over every capture
            source). The guard REUSES the org-intake routable-superset
            decision - the inbox is gated by the SAME config-backed
            owner-role mechanism server-side (org-default
            new_lead_followup routing_config row; default 'coc'; owner
            supervises org-wide). NO /me flag, NO feature flag (internal
            surface, default-on per ADR-0016). Backend route auth on
            GET /api/lead-inbox remains the security boundary.
          */}
          <Route element={<RequireLeadInbox />}>
            <Route path="lead-inbox" element={<LeadInboxPage />} />
          </Route>
          {/*
            ADORACARE-VISIT-CASE-SPINE-V1: the test-clinic journey stepper
            (lead -> patient -> visit -> diagnosis/treatment -> collected
            payment). Role-only gate (the five staff roles) via
            RequireTestJourney - a lower role hitting the URL redirects to its
            own homePath BEFORE the page renders. NO /me flag, NO feature flag,
            NO new nav section (internal surface, default-on per ADR-0016). It
            COMPOSES existing backend only (GET /api/journey/leads/:id, POST
            .../patient-link -> THE ONE conversion writer, the EXISTING booked
            create + S2-P3 check-in hops, the case-sheet endpoints behind the
            compliance wall, and POST /api/patient-payments) - backend route
            auth on each remains the security boundary.
          */}
          <Route element={<RequireTestJourney />}>
            <Route path="test-journey" element={<TestJourneyPage />} />
          </Route>
          {/*
            ADORACARE-PATIENT-MASTER-B2: the Patient Master front-desk
            surface. Guarded by the SERVER-computed
            `me.patientMasterEnabled` / `me.patientMasterMergeEnabled`
            signals — present only when PATIENT_MASTER_V1_ENABLED is on
            AND the role is in the B1-mapped audience. With the flag off
            both keys are absent, the guards redirect to the user's own
            homePath, and nothing new is reachable. Backend route auth
            (flag 503 before auth + role/org scope in the handler)
            remains the security boundary. The merge-queue route sits
            BEFORE `patients/:patientId` so the literal segment wins.
          */}
          <Route element={<RequirePatientMasterMerge />}>
            <Route
              path="patients/merge-queue"
              element={<PatientMergeQueuePage />}
            />
          </Route>
          <Route element={<RequirePatientMaster />}>
            <Route path="patients" element={<PatientSearchPage />} />
            <Route path="patients/new" element={<PatientCreatePage />} />
            {/* RECEPTION-OS-BUILD-V1 (Q-DP-PRINT=B): the consent-form print
                rail. Sits BEFORE `:patientId` so the literal segment wins.
                Content is the org's configured template or the honest
                SOURCED-PENDING placeholder -- never invented legal text. */}
            <Route
              path="patients/consent-print"
              element={<ConsentFormPrintPage />}
            />
            {/*
              PATIENT-CRM-TIMELINE-01: read-only patient 360 journey.
              Display-only (no link/merge/create). Sits BEFORE the
              `:patientId` record route so the more specific
              `:patientId/timeline` segment is reachable.
            */}
            <Route
              path="patients/:patientId/timeline"
              element={<PatientTimelinePage />}
            />
            <Route
              path="patients/:patientId"
              element={<PatientRecordPage />}
            />
          </Route>
          <Route element={<RequireNavSection section="coc" />}>
            <Route path="coc" element={<CocDashboardPage />} />
            <Route path="coc/coverage" element={<CocDashboardPage view="coverage" />} />
            <Route path="coc/clinic/:id" element={<ClinicStatusPage />} />
            <Route path="coc/billing" element={<BillingCompliancePage />} />
            {/*
              COC-REPORTING-GRAINS-V1 (activity 21): the clinic-wise +
              treatment-wise follow-up report. Same nav-section gate as the
              three doors above -- this package widens no role's access;
              the backend route reuses the SAME role gate + org/clinic
              fence + ownership-visibility filter the Follow-ups register
              already enforces (getCocPatientFollowupQueueUncapped, via
              coc-followup-report-read-model.ts), aggregated server-side
              before that register's own presented-list cap applies.
            */}
            <Route path="coc/followup-report" element={<CocFollowupReportPage />} />
            {/*
              COC-EOD-REPORT-V1 (activity 23): the date-navigable Daily
              report. Flag-free, same nav-section gate as the three doors
              above -- this package widens no role's access to anything;
              the underlying reads (fetchCocClinics / fetchTodayAppointments
              board view / fetchCocStation) are the SAME already-open reads
              CocDashboardPage, ManagerOpsCockpit and CocStationSection each
              already call for this exact role.
            */}
            <Route path="coc/daily-report" element={<CocDailyReportPage />} />
          </Route>
          {/*
            FOLLOWUP-OWNERSHIP-ROUTING-CONFIG-V1: the patient follow-ups
            queue moves off the coc nav-section guard onto the config-driven
            RequireFollowupQueue (the RequireManagerTracking decoupling
            precedent): the org's followup_ownership routing may admit the
            front desk (as in-window owner) and the doctor (window
            read-only) via the server-computed followupQueueEnabled /me
            signal, WITHOUT dumping the COC nav on them. Every 'coc'-section
            caller is admitted exactly as before (the legacy arm inside
            decideFollowupQueueAccess). Backend route auth (the queue's
            config-routed admission + clinic fences) remains the security
            boundary.
          */}
          <Route element={<RequireFollowupQueue />}>
            <Route
              path="coc/patient-followups"
              element={<PatientFollowupsPage />}
            />
          </Route>
          {/*
            FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "No Show Recovery"): the
            missed-appointment recovery queue moves off the coc nav-section
            guard onto the config-driven RequireNoShowQueue (the
            RequireFollowupQueue decoupling precedent, one route down): the
            org's Q-05=D audience config may admit the front desk via the
            server-computed missedAppointmentQueueEnabled /me signal,
            WITHOUT dumping the COC nav on them. Every 'coc'-section caller
            is admitted exactly as before (the legacy arm inside
            decideNoShowQueueAccess). Backend route auth (the queue read's
            config-audience gate + clinic fences) remains the security
            boundary.
          */}
          <Route element={<RequireNoShowQueue />}>
            <Route path="coc/no-shows" element={<NoShowRecoveryPage />} />
          </Route>
          {/*
            CRM-ROLE-VISIBILITY-DOCTRINE-V1 + OWNER-READ-DOWN: the manager
            tracking landing is subordinate OPERATIONAL work (item-9 buckets +
            clinic x metric matrix + per-staff load), so the senior operational
            tier reads it DOWN by hierarchy (doctrine rule 1). It was previously
            RequireNavSection section="agm", whose decision keys off
            me.navSections -- the SAME array that renders the left nav -- so it
            could not admit the owner without dumping the whole AgmNav into the
            owner menu (rule-3 violation). RequireManagerTracking is the
            per-surface visibility gate keyed off MANAGER_TRACKING_AUDIENCE_ROLES
            (owner / gm_agm / regional_head; frontend twin of the backend set),
            DECOUPLING route visibility from nav layout: the owner reaches the
            landing here and gets ONE owner nav entry (rule 3), never the whole
            AGM section. gm_agm / regional_head are byte-identical; only the owner
            is newly admitted. SEE-NOT-DO (rule 2): a READ gate only -- no claim /
            assign / qualify / stage authority is granted. Backend route auth
            (MANAGER_TRACKING_AUDIENCE_ROLES + requireAgmScope, which already
            admits the owner) remains the security boundary.
          */}
          <Route element={<RequireManagerTracking />}>
            <Route path="agm" element={<AgmDashboardPage />} />
            <Route path="agm/zone" element={<ZonePerformancePage />} />
            <Route path="agm/compare" element={<ClinicComparisonPage />} />
          </Route>
          {/* DOCTOR-CLINICAL-WORKSPACE-V1: the doctor home (today's queue)
              and the ROUTED consult workspace. A consult opens ONLY from the
              queue or a patient-context link (NO-MANUAL-ID LAW; guard-pinned)
              -- the backend doctor-only gate on
              /api/doctor/consult-context/:id is the security boundary. */}
          <Route element={<RequireNavSection section="doctor" />}>
            {/* DOCTOR-PRECALL-POLISH-V1 (DR-19): the doctor landing is HOME,
                not "Today" -- it composes the whole day as a dashboard, not
                one queue. The named route moves to doctor/home and the old
                route REDIRECTS (never 404s) -- the RECEPTIONIST-EXPERIENCE-
                STITCH-V1 F2 idiom below. Every existing deep link, breadcrumb
                and navigate('/dashboard/doctor') keeps working. */}
            <Route path="doctor/home" element={<DoctorPortalPage />} />
            <Route path="doctor/pending" element={<DoctorPendingPage />} />
            <Route
              path="doctor"
              element={<Navigate to="/dashboard/doctor/home" replace />}
            />
            <Route
              path="doctor/consult/:appointmentId"
              element={<ConsultWorkspacePage />}
            />
            {/* DOCTOR-OS-EXPERIENCE-V1 (B3): the treatment-consent PRINT
                rail -- the ConsentFormPrintPage sibling in the print
                family (same PrintSheet pattern), but registered under the
                DOCTOR nav guard, not RequirePatientMaster: its one data
                read (GET /api/case-sheets/:id/consents) is doctor-only, so
                the patient-master guard would make it a dead door for the
                exact role that opens it. The literal path segment wins
                over patients/:patientId (route ranking). */}
            <Route
              path="patients/treatment-consent-print"
              element={<TreatmentConsentPrintPage />}
            />
            {/* DOCTOR-WORKSPACE-COMPLETION-V1 (founder ruling 2026-07-17,
                OPTION B): the doctor lens surfaces -- patient search + the
                doctor-shaped Patient 360. Nested under the doctor nav-section
                guard AND the RequireDoctorLens signal guard (the #294 door
                pattern: the route redirects to the doctor home when the
                server-computed me.doctorLensEnabled signal is absent). The
                backend doctor-only gate on /api/doctor/patient-* is the
                security boundary. */}
            <Route element={<RequireDoctorLens />}>
              <Route
                path="doctor/patients"
                element={<DoctorPatientsPage />}
              />
              <Route
                path="doctor/patient/:patientId"
                element={<DoctorPatientLensPage />}
              />
            </Route>
          </Route>
          {/* RECEPTIONIST-EXPERIENCE-STITCH-V1 (F2): the Workstation surface
              folds into the /walkin Today home. The old route redirects (never
              404s); WorkstationPage is retained only for its WorkstationContent
              unit test, no longer mounted here. */}
          <Route element={<RequireNavSection section="receptionist" />}>
            <Route path="receptionist" element={<Navigate to="/walkin" replace />} />
          </Route>
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

export default App;
